import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import {
  EngagementFlowProperties,
  ProfileFormErrorCode,
  ProfileFormStateDto,
  ProfileFormStateFieldDto,
  ProfileFormStateSectionDto,
  ProfileFormValidationErrorDto,
} from "src/app/services/api";

/** select → option id, multiselect → option ids, checkbox → boolean, text/date → string, number → number. */
export type ProfileFormAnswerValue = string | number | boolean | string[] | null;

export type ProfileFormAnswers = Record<string, ProfileFormAnswerValue>;

export interface ProfileFormSectionErrors {
  fields: Record<string, ProfileFormErrorCode>;
  section: ProfileFormErrorCode | null;
}

const E164_PHONE = /^\+[1-9]\d{6,14}$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Form mode: the merchant built a profile form. Legacy flows only list `requiredFields`. */
export const isProfileFormMode = (properties: EngagementFlowProperties | null | undefined): boolean => properties?.form != null;

/** Sections the customer is done with: answered or "not interested". */
export const countDoneSections = (state: ProfileFormStateDto | null | undefined): number =>
  (state?.sections ?? []).filter(({ status }) => status === "complete" || status === "dismissed").length;

/** The generated client types the answer maps as `object` values; the API sends plain JSON values. */
export const getInitialAnswers = (state: ProfileFormStateDto): ProfileFormAnswers => ({
  ...((state.prefill ?? {}) as unknown as ProfileFormAnswers),
  ...((state.answers ?? {}) as unknown as ProfileFormAnswers),
});

/** Today in the customer's time zone, as the API compares birthdates: `YYYY-MM-DD`. */
export const getLocalIsoDate = (date: Date = new Date()): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** Converts a form control value into the value the API expects for the field. */
export const toAnswerValue = (field: ProfileFormStateFieldDto, raw: unknown): ProfileFormAnswerValue => {
  switch (field.type) {
    case "checkbox":
      return raw === true;
    case "multiselect":
      return Array.isArray(raw) && raw.length > 0 ? (raw as string[]) : null;
    case "number": {
      if (typeof raw === "number") return raw;
      const text = typeof raw === "string" ? raw.trim().replace(",", ".") : "";
      return text ? Number(text) : null;
    }
    case "date":
      return typeof raw === "string" && raw ? raw.substring(0, 10) : null;
    default: {
      const text = typeof raw === "string" ? raw.trim() : "";
      return text || null;
    }
  }
};

export const buildSectionAnswers = (section: ProfileFormStateSectionDto, rawValues: Record<string, unknown>): ProfileFormAnswers =>
  Object.fromEntries(section.fields.map(field => [field.id, toAnswerValue(field, rawValues[field.id])]));

const isValidIsoDate = (value: string): boolean => {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

const validateAnswer = (field: ProfileFormStateFieldDto, value: ProfileFormAnswerValue, today: string): ProfileFormErrorCode | null => {
  if (field.type === "checkbox") return field.required && value !== true ? "REQUIRED" : null;
  if (value === null) return field.required ? "REQUIRED" : null;

  const optionIds = (field.options ?? []).map(option => option.id);

  switch (field.type) {
    case "select":
      return optionIds.includes(value as string) ? null : "INVALID_OPTION";
    case "multiselect": {
      const selected = value as string[];
      if (selected.some(id => !optionIds.includes(id))) return "INVALID_OPTION";
      if (field.minSelected != null && selected.length < field.minSelected) return "MIN_SELECTED";
      if (field.maxSelected != null && selected.length > field.maxSelected) return "MAX_SELECTED";
      return null;
    }
    case "number": {
      const number = value as number;
      if (!Number.isFinite(number)) return "INVALID_VALUE";
      return field.integer && !Number.isInteger(number) ? "INVALID_VALUE" : null;
    }
    case "date": {
      const date = value as string;
      if (!isValidIsoDate(date)) return "INVALID_VALUE";
      return field.userDataKey === "birthdate" && date > today ? "FUTURE_DATE" : null;
    }
    default:
      return field.userDataKey === "phone" && !E164_PHONE.test(value as string) ? "INVALID_PHONE" : null;
  }
};

/** Same rules the API applies on "submit", so the customer gets the feedback before sending. */
export const validateSection = (
  section: ProfileFormStateSectionDto,
  answers: ProfileFormAnswers,
  today: string = getLocalIsoDate(),
): Record<string, ProfileFormErrorCode> =>
  section.fields.reduce<Record<string, ProfileFormErrorCode>>((errors, field) => {
    const code = validateAnswer(field, answers[field.id] ?? null, today);
    return code ? { ...errors, [field.id]: code } : errors;
  }, {});

/** Validation errors of a 400 `PROFILE_FORM_INVALID` (at the root of the body or under `response`). */
export const getProfileFormErrors = (error: HttpErrorResponse | null | undefined): ProfileFormValidationErrorDto[] => {
  const body = error?.error?.errors ? error.error : error?.error?.response;
  return body?.message === "PROFILE_FORM_INVALID" && Array.isArray(body.errors) ? body.errors : [];
};

export const getSectionErrors = (errors: ProfileFormValidationErrorDto[], sectionId: string): ProfileFormSectionErrors =>
  errors
    .filter(error => error.sectionId === sectionId)
    .reduce<ProfileFormSectionErrors>(
      (grouped, error) => error.fieldId
        ? { ...grouped, fields: { ...grouped.fields, [error.fieldId]: error.code } }
        : { ...grouped, section: error.code },
      { fields: {}, section: null },
    );

/** The merchant changed the form while the customer was answering it: reload it and ask to review. */
export const isProfileFormOutdated = (error: HttpErrorResponse | null | undefined): boolean =>
  error?.status === HttpStatusCode.Conflict || getProfileFormErrors(error).some(({ code }) => code === "UNKNOWN_SECTION");
